import { AuthenticationStore } from "./authenticationStore";
import { accessTokenExpiringLeewaySeconds, getUserManager } from "./user-manager";

const refreshLockName = "istari-token-refresh";
const lockWaitTimeoutMs = 15_000;
const lostRacePollAttempts = 6;
const lostRacePollIntervalMs = 500;

let refreshPromise: Promise<void> | undefined;
let refreshLeewaySeconds = 0;

/**
 * Single owner of access-token renewal: deduplicated within the tab and serialized
 * across tabs by a Web Lock, so a single-use refresh token is never spent twice.
 * Renewal goes through the shared UserManager, so storage, the in-memory user, and
 * auth events stay consistent. Never rejects — a caller that cannot be given a new
 * token proceeds with the one it has.
 *
 * Renews only when the stored token expires within `leewaySeconds`. The default
 * matches the accessTokenExpiring event; a caller about to pin the token for a long
 * operation passes a wider window. A caller whose window fits inside an in-flight
 * renewal's shares it; a wider window queues its own renewal behind it, which
 * `renewIfStillDue` turns into a no-op if the in-flight one already renewed.
 */
export const refreshAccessToken = async (leewaySeconds: number = accessTokenExpiringLeewaySeconds): Promise<void> => {
  const promise =
    refreshPromise !== undefined && refreshLeewaySeconds >= leewaySeconds
      ? refreshPromise
      : queueRefresh(leewaySeconds);
  try {
    await promise;
  } catch {
    // Best effort: the 401 recovery path handles a token that is really dead.
  }
};

/** Runs one renewal, after the pending one if there is one, so a tab never has two exchanges in flight. */
function queueRefresh(leewaySeconds: number): Promise<void> {
  const run = () => runExclusiveRefresh(leewaySeconds);
  const promise: Promise<void> = (refreshPromise ? refreshPromise.catch(() => undefined).then(run) : run()).finally(
    () => {
      if (refreshPromise === promise) {
        refreshPromise = undefined;
      }
    },
  );
  refreshPromise = promise;
  refreshLeewaySeconds = leewaySeconds;
  return promise;
}

/** Whether the stored `expires_at` is already in the past. Reads storage on every call. */
export function isStoredAccessTokenExpired(): boolean {
  return isStoredAccessTokenDueForRenewal(0);
}

async function runExclusiveRefresh(leewaySeconds: number): Promise<void> {
  if (typeof navigator !== "undefined" && navigator.locks) {
    // Every request waits on this lock via the axios interceptor, so a frozen tab holding it
    // must not stall the others indefinitely. Aborting resolves the caller with its current token.
    await navigator.locks.request(refreshLockName, { signal: AbortSignal.timeout(lockWaitTimeoutMs) }, () =>
      renewIfStillDue(leewaySeconds),
    );
    return;
  }
  await renewIfStillDue(leewaySeconds);
}

async function renewIfStillDue(leewaySeconds: number): Promise<void> {
  if (!isStoredAccessTokenDueForRenewal(leewaySeconds)) {
    return;
  }

  if (!AuthenticationStore().getRefreshToken()) {
    await endSession();
    return;
  }

  try {
    await getUserManager().signinSilent();
  } catch (error) {
    if (!isInvalidGrantError(error)) {
      // Transient failure (network, 5xx): keep the current token, next request retries.
      return;
    }
    if (await awaitRenewalByAnotherOwner(leewaySeconds)) {
      return;
    }
    await endSession();
  }
}

/**
 * A rejected refresh token can never recover. Unloading the user clears storage and
 * flips isAuthenticated so the router redirects to sign-in, instead of every request
 * paying a doomed refresh until the JWT's real expiry turns into Expired Token 401s.
 */
async function endSession(): Promise<void> {
  await getUserManager().removeUser();
}

/**
 * With rotating single-use tokens, invalid_grant usually means a renewer outside the
 * Web Lock spent the refresh token moments ago. Its result may not have reached
 * storage yet, so poll briefly before declaring the session dead.
 *
 * Recovery means no longer due for renewal, not merely unexpired: on the proactive path
 * the token is still valid for the whole leeway window, so an expiry check would report
 * recovery even when storage never moved. Holds the lock, hence the bounded attempts.
 */
async function awaitRenewalByAnotherOwner(leewaySeconds: number): Promise<boolean> {
  for (let attempt = 0; attempt < lostRacePollAttempts; attempt++) {
    if (attempt > 0) {
      await delay(lostRacePollIntervalMs);
    }
    if (!isStoredAccessTokenDueForRenewal(leewaySeconds)) {
      return true;
    }
  }
  return false;
}

/** Whether the stored `expires_at` is within `leewaySeconds` of now. Reads storage on every call. */
export function isStoredAccessTokenDueForRenewal(leewaySeconds: number): boolean {
  const expiresAt = AuthenticationStore().getAccessTokenExpires();
  if (!expiresAt) {
    return false;
  }
  return Date.now() > (expiresAt - leewaySeconds) * 1000;
}

function delay(milliseconds: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, milliseconds));
}

function isInvalidGrantError(error: unknown): boolean {
  return typeof error === "object" && error !== null && "error" in error && error.error === "invalid_grant";
}
